import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { cloneDeep } from 'lodash'

import {
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Fab,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material'

import { Add } from '@mui/icons-material'
import FormLabel from '@mui/material/FormLabel'
import { addKeyToObjectsArray } from 'utils/addUniqueKeyToObjects'
import { pepRelationshipTypes } from 'utils/constants/DueDiligenceRelations'
import { formatInBrazilianDate, hasAnswerChanged } from 'utils/functions'
import { OPERATION_TYPES } from 'utils/constants/operationTypes'

import styles from './styles'
import AnswerCard from '../AnswerCard'
import QuestionButton from '../QuestionButton'

export default function QuestionTen(props) {
  const { questionData, saveResponses } = props
  const classes = styles()

  const initialData = {
    pep_name: '',
    pep_type: 'holder',
    kinship_degree: null,
    role: '',
    developed_activities: '',
    organization: '',
    reports_to: '',
    currently_at_this_job: false,
    start_working_hours: null,
    end_working_hours: null,
    end_of_contract: null,
    investment_advisory_start_time: null,
    investment_advisory_end_time: null,
  }

  const initialErrors = {
    pep_name: false,
    kinship_degree: false,
    role: false,
    developed_activities: false,
    organization: false,
    reports_to: false,
    currently_at_this_job: false,
    start_working_hours: false,
    end_working_hours: false,
    end_of_contract: false,
    investment_advisory_start_time: false,
    investment_advisory_end_time: false,
  }

  const initializeFormState = (data) => {
    return {
      hasPEPRelations: data.items.length > 0,
      items: data.items,
    }
  }

  const [formState, setFormState] = useState(initializeFormState(questionData))

  const [activityData, setActivityData] = useState(initialData)

  const [activityErrors, setActivityErrors] = useState(initialErrors)

  const [loading, setLoading] = useState()

  const [edited, setEdited] = useState(false)
  const [saved, setSaved] = useState(questionData.has_answer)

  const handleFieldChange = (event) => {
    const key = event.target.id
    const newActivityData = cloneDeep(activityData)
    newActivityData[key] = event.target.value
    setActivityData(newActivityData)
  }

  const handleRadioChange = (event) => {
    const newFormState = cloneDeep(formState)
    newFormState.hasPEPRelations = event.target.value === 'yes'
    setFormState(newFormState)
  }

  const handlePEPTypeChange = (event) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.pep_type = event.target.value
    setActivityData(newActivityData)
  }

  const handleStillOnJobRadioChange = (event) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.currently_at_this_job = event.target.value === 'yes'
    setActivityData(newActivityData)
  }

  const handleSelectChange = (event) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.kinship_degree = event.target.value
    setActivityData(newActivityData)
  }

  const hasPEPRelations = () => {
    return !(formState.items.length === 0)
  }

  const validateAndSave = async (skipValidation = false) => {
    if (formState.items.length > 0 && formState.hasPEPRelations === false) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
      return
    }
    const newQuestionData = cloneDeep(questionData)
    if (!skipValidation && formState.hasPEPRelations && !hasPEPRelations()) {
      toast.error(
        'Ao selecionar "Sim" deve-se adicionar ao menos uma atividade',
      )
    } else {
      newQuestionData.has_answer = true
      newQuestionData.items = formState.items
      setLoading(true)
      await saveResponses(
        newQuestionData,
        'pep_relationship',
        !saved ? OPERATION_TYPES.CREATE : OPERATION_TYPES.UPDATE,
      )
        .then(() => {
          setSaved(true)
          setEdited(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const validateRequiredFields = () => {
    const newActivityErrors = cloneDeep(initialErrors)

    Object.keys(initialErrors).forEach((key) => {

      if (
        key === 'kinship_degree' &&
        activityData.pep_type !== 'holder' &&
        activityData[key] === 'default'
      ) {
        newActivityErrors[key] = true
        return
      }
      if (activityData[key] === '' || activityData[key] === null) {
        if (
          activityData.currently_at_this_job === false &&
          (key === 'start_working_hours' || key === 'end_working_hours')
        ) {
          newActivityErrors[key] = false
          return
        }
        if (
          activityData.currently_at_this_job === true &&
          key === 'end_of_contract'
        ) {
          newActivityErrors[key] = false
          return
        }

        if (
          activityData.pep_type !== 'holder' &&
          (key === 'investment_advisory_end_time' ||
            key === 'investment_advisory_start_time')
        ) {
          newActivityErrors[key] = false
          return
        }

        if (
          activityData.pep_type === 'holder' &&
          key === 'kinship_degree'
        ) {
          newActivityErrors[key] = false
          return
        }

        newActivityErrors[key] = true
      }
    })

    setActivityErrors(newActivityErrors)

    return !Object.keys(newActivityErrors).some((key) => newActivityErrors[key])
  }

  const handleValidateAndUpdateState = () => {
    if (validateRequiredFields()) {
      const newFormState = cloneDeep(formState)
      newFormState.items.push(activityData)
      setFormState(newFormState)
      setActivityData(initialData)
    } else {
      toast.warn(
        'Favor, verifique se todos os campos obrigatórios foram preenchidos corretamente',
      )
    }
  }

  const handleRemoveActivity = (position) => {
    const newFormState = cloneDeep(formState)
    newFormState.items = newFormState.items.filter(
      (activity, index) => index !== position,
    )
    setFormState(newFormState)
  }

  const buildActivity = (activityInfo) => {
    return (
      <React.Fragment>
        <p>
          <span>Nome completo do PEP: </span>
          {activityInfo.pep_name}
        </p>
        {activityInfo.pep_type === 'related' ? (
          <p>
            <span>Natureza do relacionamento: </span>
            {pepRelationshipTypes[activityInfo.kinship_degree]}
          </p>
        ) : null}
        <p>
          <span>Cargo: </span>
          {activityInfo.role}
        </p>

        <p>
          <span>Atividades desempenhadas: </span>
          {activityInfo.developed_activities}
        </p>
        <p>
          <span>Reporta/reportava-se a: </span>
          {activityInfo.reports_to}
        </p>
        {activityInfo.end_of_contract ? (
          <p>
            <span>Fim do vínculo: </span>
            {formatInBrazilianDate(activityInfo.end_of_contract)}
          </p>
        ) : null}

        {activityInfo.currently_at_this_job ? (
          <p>
            <span>Jornada de trabalho: </span>
            {` ${activityInfo.start_working_hours} às ${activityInfo.end_working_hours}.`}
          </p>
        ) : null}
        {activityInfo.pep_type === 'holder' ? (
          <p>
            <span>Jornada da atividade de assessoria: </span>
            {` ${activityInfo.investment_advisory_start_time} às ${activityInfo.investment_advisory_end_time}.`}
          </p>
        ) : null}
      </React.Fragment>
    )
  }

  useEffect(() => {
    const hasChanged =
      hasAnswerChanged(questionData.items, formState.items) ||
      formState.hasPEPRelations !== questionData.items.length > 0
    setEdited(hasChanged)
    if (formState.items.length > 0 && formState.hasPEPRelations === false) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
    }
  }, [formState])

  useEffect(() => {
    setSaved(questionData.has_answer)
    setFormState(initializeFormState(questionData))
  }, [questionData])

  useEffect(() => {
    if (hasAnswerChanged(questionData.items, formState.items))
      validateAndSave(true)
  }, [formState.items])

  return (
    <div className={classes.root}>
      <div className={classes.formContainer}>
        <h3>Questão 10</h3>
        <div className={classes.form}>
          <FormControl>
            <FormLabel>
              Você é, foi, ou tem proximidade com pessoas que são ou foram
              considerados, nos últimos 5 (cinco) anos, pessoas expostas
              politicamente? (&#34;PEP&#34;)?{' '}
            </FormLabel>
            <RadioGroup
              row
              value={formState.hasPEPRelations ? 'yes' : 'no'}
              name="hasRelations"
              id="hasRelations"
              onChange={handleRadioChange}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Sim" />
              <FormControlLabel value="no" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>

          {formState.hasPEPRelations ? (
            <div className={classes.formFields}>
              <TextField
                id="pep_name"
                label="Nome completo da pessoa exposta politicamente"
                onChange={handleFieldChange}
                required
                fullWidth
                value={activityData.pep_name}
                error={activityErrors.pep_name}
                size="small"
              />
              <FormControl className={classes.radioQuestion}>
                <FormLabel>
                  Trata-se de uma situação de PEP Direto (você exerce a função)
                  ou Indireto (função exercida por familiar ou terceiro){' '}
                </FormLabel>
                <RadioGroup
                  row
                  value={activityData.pep_type}
                  name="pep_type"
                  id="pep_type"
                  onChange={handlePEPTypeChange}
                >
                  <FormControlLabel
                    value="holder"
                    control={<Radio />}
                    label="Direto"
                  />
                  <FormControlLabel
                    value="related"
                    control={<Radio />}
                    label="Indireto"
                  />
                </RadioGroup>
              </FormControl>

              {activityData.pep_type === 'related' ? (
                <Select
                  onChange={handleSelectChange}
                  value={activityData.kinship_degree || 'default'}
                  error={activityErrors.kinship_degree}
                  size="small"
                >
                  <MenuItem value="default" disabled>
                    Selecione a natureza do relacionamento
                  </MenuItem>
                  {Object.keys(pepRelationshipTypes).map((key) => (
                    <MenuItem key={key} value={key}>
                      {pepRelationshipTypes[key]}
                    </MenuItem>
                  ))}
                </Select>
              ) : null}

              <TextField
                id="role"
                label="Cargo"
                onChange={handleFieldChange}
                required
                value={activityData.role}
                error={activityErrors.role}
                fullWidth
                size="small"
              />

              <TextField
                id="developed_activities"
                label="Atividades desempenhadas"
                onChange={handleFieldChange}
                required
                value={activityData.developed_activities}
                error={activityErrors.developed_activities}
                fullWidth
                size="small"
              />

              <TextField
                id="organization"
                label="Orgão onde exerce/exerceu atividade"
                onChange={handleFieldChange}
                required
                value={activityData.organization}
                error={activityErrors.organization}
                fullWidth
                size="small"
              />

              <TextField
                id="reports_to"
                label="A quem se reporta/reportava na estrutura organizacional do órgão?"
                onChange={handleFieldChange}
                error={activityErrors.reports_to}
                value={activityData.reports_to}
                required
                fullWidth
                size="small"
              />

              <FormControl className={classes.radioQuestion}>
                <FormLabel id="">Continua exercendo essa função?</FormLabel>
                <RadioGroup
                  row
                  value={activityData.currently_at_this_job ? 'yes' : 'no'}
                  name="currently_at_this_job"
                  onChange={handleStillOnJobRadioChange}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Sim"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label="Não"
                  />
                </RadioGroup>
              </FormControl>
              {activityData.currently_at_this_job ? (
                <div className={classes.times}>
                  <FormLabel>Jornada de trabalho:</FormLabel>
                  <div>
                    <TextField
                      id="start_working_hours"
                      label="Início"
                      type="time"
                      onChange={handleFieldChange}
                      error={activityErrors.start_working_hours}
                      value={activityData.start_working_hours || ''}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />

                    <TextField
                      id="end_working_hours"
                      label="Fim"
                      type="time"
                      onChange={handleFieldChange}
                      error={activityErrors.end_working_hours}
                      value={activityData.end_working_hours || ''}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </div>
                </div>
              ) : (
                <TextField
                  id="end_of_contract"
                  label="Data em que o vínculo foi encerrado"
                  onChange={handleFieldChange}
                  error={activityErrors.end_of_contract}
                  value={activityData.end_of_contract || ''}
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              )}
              {activityData.pep_type === 'holder' ? (
                <div className={classes.times}>
                  <FormLabel>Jornada da atividade de assessoria:</FormLabel>
                  <div>
                    <TextField
                      id="investment_advisory_start_time"
                      label="Início"
                      type="time"
                      onChange={handleFieldChange}
                      error={activityErrors.investment_advisory_start_time}
                      value={activityData.investment_advisory_start_time || ''}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />

                    <TextField
                      id="investment_advisory_end_time"
                      label="Fim"
                      type="time"
                      onChange={handleFieldChange}
                      error={activityErrors.investment_advisory_end_time}
                      value={activityData.investment_advisory_end_time || ''}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </div>
                </div>
              ) : null}

              <div className={classes.fabContainer}>
                <Tooltip title="Adicionar atividade">
                  <Fab
                    onClick={handleValidateAndUpdateState}
                    disabled={formState?.items?.length === 10}
                    size="small"
                    color="primary"
                  >
                    <Add />
                  </Fab>
                </Tooltip>
              </div>
            </div>
          ) : null}
        </div>
        {formState?.items?.length > 0 ? (
          <div className={classes.answersContainer}>
            {addKeyToObjectsArray(formState.items)?.map((activity, index) => (
              <AnswerCard
                key={activity.key}
                index={index}
                handleRemove={handleRemoveActivity}
                mustRemove={formState.hasPEPRelations === false}
              >
                {buildActivity(activity)}
              </AnswerCard>
            ))}
          </div>
        ) : null}
      </div>
      <div className={classes.buttonsContainer}>
        <QuestionButton
          variant="contained"
          onClick={validateAndSave}
          loading={loading}
          saved={saved}
          edited={edited}
          disabled={
            (formState.hasPEPRelations && formState?.items?.length === 0) ||
            (saved && !edited)
          }
        />
      </div>
    </div>
  )
}
